import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { PasswordResetService } from '../api/generated/requests/services.gen';

const ResetPasswordRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const logoSvg = useMemo(() => (
    <svg
      width="56"
      height="56"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="ustalone-logo-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#06b6d4" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill="url(#ustalone-logo-grad)" />
      <path
        d="M14 21.5L19 26L27 16"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ), []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await PasswordResetService.postApiAuthPasswordResetRequest({
        requestBody: { email }
      });

      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Błąd podczas wysyłania żądania resetowania hasła:', err);

      if (err.status === 404) {
        setError('Nie znaleziono konta z podanym adresem email.');
      } else {
        setError('Wystąpił błąd podczas wysyłania linku resetującego hasło. Spróbuj ponownie później.');
      }

      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
      }}
    >
      {/* LEFT COLUMN */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          px: { xs: 4, md: 8 },
          py: 6,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            {logoSvg}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                color: 'primary.main',
              }}
            >
              ustalone
            </Typography>
          </Box>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 5}}>
            Przywracanie hasła
          </Typography>

          <Typography variant="body1" sx={{ mb: 4 }}>
            <strong>Ustalone</strong> to <strong>nowoczesny system CRM</strong>, który pomaga zarządzać relacjami z klientami, automatyzować sprzedaż i rozwijać Twój biznes. Podaj adres email, aby zresetować hasło.
          </Typography>

          {success ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Link do resetowania hasła został wysłany na podany adres email.
              </Alert>
              <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                Sprawdź swoją skrzynkę pocztową i kliknij w link resetujący, aby ustawić nowe hasło.
              </Typography>
              <Link to="/login" style={{ color: 'inherit', textDecoration: 'underline', fontSize: '0.875rem' }}>
                Powrót do strony logowania
              </Link>
            </Box>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                variant="standard"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                autoFocus
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Link to="/login" style={{ color: 'inherit', textDecoration: 'underline', fontSize: '0.875rem' }}>
                  Powrót do logowania
                </Link>
                <Button type="submit" variant="contained" disabled={loading || !email}>
                  {loading ? 'Wysyłanie...' : 'Wyślij link resetujący'}
                </Button>
              </Box>
            </form>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          Copyright © 2025 Devlab Sp. z o.o.
        </Typography>
      </Box>

      {/* RIGHT COLUMN - Tylko obrazek */}
      {!isMobile && (
        <Box
          sx={{
            flex: 1,
            backgroundImage: 'url(/crm-login.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
    </Box>
  );
};

export default ResetPasswordRequestPage;
